import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface DocHubFile {
  id: string;
  brand_id: string;
  user_id: string;
  name: string;
  link: string;
  file_type: 'uploaded' | 'generated';
  images?: any;
  category_id?: string;
  tags: string[];
  slug?: string;
  created_at: string;
  updated_at: string;
}

export const useDocHubFiles = (brandId: string) => {
  const queryClient = useQueryClient();

  const {
    data: files,
    isLoading,
    error
  } = useQuery({
    queryKey: ['doc-hub-files', brandId],
    queryFn: async (): Promise<DocHubFile[]> => {
      const { data, error } = await supabase
        .from('os_doc_hub_files')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return (data || []).map(file => ({
        ...file,
        tags: Array.isArray(file.tags) ? file.tags : 
              typeof file.tags === 'string' ? JSON.parse(file.tags) : [],
        file_type: (file.file_type || 'uploaded') as 'uploaded' | 'generated',
      }));
    },
    enabled: !!brandId,
  });

  const createFileMutation = useMutation({
    mutationFn: async (fileData: Omit<DocHubFile, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('os_doc_hub_files')
        .insert({
          ...fileData,
          tags: fileData.tags,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-files', brandId] });
      toast({
        title: "Success",
        description: "File added successfully",
      });
    },
    onError: (error) => {
      console.error('Error creating file:', error);
      toast({
        title: "Error",
        description: "Failed to add file",
        variant: "destructive",
      });
    },
  });

  const updateFileMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<DocHubFile> & { id: string }) => {
      const updateData: any = { ...updates };
      if (updates.tags !== undefined) {
        updateData.tags = updates.tags;
      }

      const { data, error } = await supabase
        .from('os_doc_hub_files')
        .update(updateData)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-files', brandId] });
      toast({
        title: "Success",
        description: "File updated successfully",
      });
    },
    onError: (error) => {
      console.error('Error updating file:', error);
      toast({
        title: "Error",
        description: "Failed to update file",
        variant: "destructive",
      });
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: async (fileId: string) => {
      const { error } = await supabase
        .from('os_doc_hub_files')
        .delete()
        .eq('id', fileId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-files', brandId] });
      toast({
        title: "Success",
        description: "File deleted successfully",
      });
    },
    onError: (error) => {
      console.error('Error deleting file:', error);
      toast({
        title: "Error",
        description: "Failed to delete file",
        variant: "destructive",
      });
    },
  });

  const createFile = (fileData: Omit<DocHubFile, 'id' | 'created_at' | 'updated_at'>) => {
    createFileMutation.mutate(fileData);
  };

  const updateFile = (id: string, updates: Partial<DocHubFile>) => {
    updateFileMutation.mutate({ id, ...updates });
  };

  const deleteFile = (id: string) => {
    deleteFileMutation.mutate(id);
  };

  return {
    files: files || [],
    isLoading,
    error,
    createFile,
    updateFile,
    deleteFile,
    isCreating: createFileMutation.isPending,
    isUpdating: updateFileMutation.isPending,
    isDeleting: deleteFileMutation.isPending,
  };
};
